import { ApiAnalyticsAggregationDate } from "@/types/analytics/analytics.types.ts";
import { ApiEmployee, ApiEmployeeLifecycleStatus } from "@/types/employees/employee.types.ts";
import { ApiFilterBase } from "@skailer-com/uikit/core";
import { CompensationRollupType } from "@/types/analytics/analytics.employee.types.ts";

export enum ApiWidgetType {
  kpi = "kpi",
  line_chart = "line_chart",
  bar_chart = "bar_chart",
  pie_chart = "pie_chart",
  table = "table",
}

export enum ApiWidgetMetric {
  headcount = "headcount",
  hires = "hires",
  terminations = "terminations",
  headcount_growth_rate = "headcount_growth_rate",
  turnover_rate = "turnover_rate",
  attrition_rate = "attrition_rate",
  retention_rate = "retention_rate",
  average_tenure = "average_tenure",
  average_age = "average_age",
  manager_to_employee_ratio = "manager_to_employee_ratio",
  managers_count = "managers_count",
  total_payroll = "total_payroll",
  average_salary = "average_salary",
  median_salary = "median_salary",
  total_bonus = "total_bonus",
  total_cost = "total_cost",
  employer_contributions = "employer_contributions",
}

export enum ApiWidgetDimension {
  department = "department",
  division = "division",
  location = "location",
  job_profile = "job_profile",
  job_role = "job_role",
  termination_leave_type = "termination_leave_type",
  termination_reason = "termination_reason",
  age_group = "age_group",
  gender = "gender",
  salary_last_updated = "salary_last_updated",
}

export enum ApiWidgetColumnKey {
  period = "period",
  headcount = "headcount",
  hires = "hires",
  terminations = "terminations",
  headcount_growth_rate = "headcount_growth_rate",
  turnover_rate = "turnover_rate",
  attrition_rate = "attrition_rate",
  retention_rate = "retention_rate",
  average_tenure = "average_tenure",
  average_age = "average_age",
  manager_to_employee_ratio = "manager_to_employee_ratio",
  managers_count = "managers_count",
  total_payroll = "total_payroll",
  average_salary = "average_salary",
  median_salary = "median_salary",
  total_bonus = "total_bonus",
  total_cost = "total_cost",
  employer_contributions = "employer_contributions",
  department = "department",
  division = "division",
  location = "location",
  job_profile = "job_profile",
  job_role = "job_role",
  termination_leave_type = "termination_leave_type",
  termination_reason = "termination_reason",
  age_group = "age_group",
  gender = "gender",
  salary_last_updated = "salary_last_updated",
  // Frontend-only: synthesized by widgetDimensionToTableData, never sent by the backend.
  employees = "employees",
}

export enum ApiWidgetPeriodMode {
  absolute = "absolute",
  relative = "relative",
}

export enum ApiWidgetRelativePeriod {
  last_30_days = "last_30_days",
  last_quarter = "last_quarter",
  last_6_months = "last_6_months",
  last_12_months = "last_12_months",
  year_to_date = "year_to_date",
}

export enum ApiWidgetBucketSort {
  value_desc = "value_desc",
  value_asc = "value_asc",
  natural = "natural",
}

export enum ApiWidgetFilterField {
  job_profile_ids = "job_profile_ids",
  job_role_ids = "job_role_ids",
  department_ids = "department_ids",
  division_ids = "division_ids",
  location_ids = "location_ids",
  employment_type_ids = "employment_type_ids",
  employment_contract_ids = "employment_contract_ids",
}

export interface ApiWidgetFilter {
  field: ApiWidgetFilterField;
  ids: number[];
}

export interface ApiWidgetPeriodInput {
  mode: ApiWidgetPeriodMode;
  value?: ApiWidgetRelativePeriod | null;
  date_from?: Date | null;
  date_to?: Date | null;
}

export interface ApiWidgetCompensationOptions {
  /** @deprecated Use rollup_type. */
  salary_type?: "gross" | "net";
  rollup_type?: CompensationRollupType;
  currency?: string;
}

export interface ApiWidgetQueryConfigInput {
  metrics: ApiWidgetMetric[];
  dimensions?: ApiWidgetDimension[];
  filters?: ApiWidgetFilter[];
  date_grain?: ApiAnalyticsAggregationDate;
  limit?: number;
  hide_zero_buckets?: boolean;
  bucket_sort?: ApiWidgetBucketSort;
  compensation?: ApiWidgetCompensationOptions | null;
}

export interface ApiWidgetPreviewInput {
  version?: number;
  widget_type: ApiWidgetType;
  period: ApiWidgetPeriodInput;
  query: ApiWidgetQueryConfigInput;
}

export interface ApiWidgetDataPoint {
  label: string;
  entity_id: number | null;
  value: number | null;
  entity_ids: number[];
  comparison_value: number | null;
  comparison_entity_ids: number[];
  date_from: Date | null;
  date_to: Date | null;
}

export interface ApiWidgetSeries {
  key: string;
  entity_id: number | null;
  points: ApiWidgetDataPoint[];
}

export interface ApiWidgetColumn {
  key: ApiWidgetColumnKey;
}

export interface ApiWidgetDataMeta {
  datetime_generated_at: Date;
  warnings: string[];
  currency: string | null;
}

export interface ApiWidgetData {
  metrics: ApiWidgetMetric[];
  value: number | null;
  comparison_value: number | null;
  series: ApiWidgetSeries[];
  columns: ApiWidgetColumn[];
  rows: (string | number | null)[][];
  date_from: Date;
  date_to: Date;
  meta: ApiWidgetDataMeta;
}

export enum ApiReportingDetailsSortField {
  employee_name = "employee_name",
  salary_gross = "salary_gross",
  salary_net = "salary_net",
  last_updated = "last_updated",
  period_growth = "period_growth",
  lifetime_growth = "lifetime_growth",
}

export enum ApiReportingDetailsSortDirection {
  asc = "asc",
  desc = "desc",
}

export enum ApiReportingDetailsFormat {
  page = "page",
  xlsx = "xlsx",
  csv = "csv",
  pdf = "pdf",
}

export interface ApiReportingDetailsSortingField {
  field: ApiReportingDetailsSortField;
  direction: ApiReportingDetailsSortDirection;
}

export interface ApiReportingDetailsInput extends ApiFilterBase {
  period: ApiWidgetPeriodInput;
  compensation: ApiWidgetCompensationOptions;
  filters?: ApiWidgetFilter[];
  employee_lifecycle_statuses?: ApiEmployeeLifecycleStatus[];
  text?: string | null;
  sorting?: ApiReportingDetailsSortingField[];
  format?: ApiReportingDetailsFormat;
}

export interface ApiReportingMoneyValue {
  currency: string | null;
  value: number;
}

export interface ApiReportingSalaryDetails {
  employee: ApiEmployee;
  salary_gross: ApiReportingMoneyValue | null;
  salary_gross_original: ApiReportingMoneyValue | null;
  salary_net: ApiReportingMoneyValue | null;
  salary_net_original: ApiReportingMoneyValue | null;
  salary_total_cost: ApiReportingMoneyValue | null;
  salary_total_cost_original: ApiReportingMoneyValue | null;
  employer_contributions: ApiReportingMoneyValue | null;
  employer_contributions_original: ApiReportingMoneyValue | null;
  date_last_updated: Date | null;
  period_growth: number | null;
  lifetime_growth: number | null;
}
